import {
  buildDatastorePayload,
  normalizeDatastorePayloadFromRaw
} from "@/helpers/labAttemptPersistence.js";

/**
 * Per-lab attempt behaviour configuration.
 *
 * maxAttempts       — how many attempt slots exist for this lab (default 5).
 * locksOnCompletion — when true, a completed attempt is read-only and the student
 *                     must start a new one. When false, the lab never locks: the
 *                     student can click through from the beginning again on the same
 *                     attempt (useful for single-attempt demo/exploration labs).
 *
 * Add or adjust entries here to configure any lab without touching other files.
 */
const DEFAULT_LAB_CONFIG = { maxAttempts: 5, locksOnCompletion: true };
const LAB_CONFIG_BY_LAB = {
  "ft-ir-spectroscopy-lab": { maxAttempts: 1, locksOnCompletion: false }
};

function getActiveLabConfig() {
  const labId = import.meta.env.VITE_LAB_ENV;
  return { ...DEFAULT_LAB_CONFIG, ...(LAB_CONFIG_BY_LAB[labId] ?? {}) };
}

const {
  maxAttempts: INITIAL_MAX_ATTEMPTS,
  locksOnCompletion: INITIAL_LOCKS_ON_COMPLETION
} = getActiveLabConfig();

const app = {
  state: {
    theme: localStorage.getItem("theme") || "light",
    labAttempts: [],
    currentAttemptId: null,
    maxAttempts: INITIAL_MAX_ATTEMPTS,
    locksOnCompletion: INITIAL_LOCKS_ON_COMPLETION,
    context: null,
    showDebugUI: false,
    showSimpleUI: false,
    inMobileView: false,
    /** Loaded workbook manifest (for header progress bar); set by useStoryEngine */
    labManifest: null,
    /** True while a StoryEngine instance is running (for header progress visibility) */
    storyEngineActive: false
  },

  mutations: {
    SET_THEME(state, theme) {
      state.theme = theme;
      localStorage.setItem("theme", theme);
      document.documentElement.setAttribute("data-bs-theme", theme);
    },
    ADD_LAB_ATTEMPT(state, attempt) {
      state.labAttempts.push({
        attemptId: attempt.attemptId,
        timestamp: attempt.timestamp,
        isComplete: false,
        completedAt: null,
        measurements: attempt.measurements || [],
        formData: attempt.formData || {},
        solutions: attempt.solutions || {}
      });
    },
    UPDATE_LAB_ATTEMPT(state, { attemptId, updates }) {
      const attempt = state.labAttempts.find((a) => a.attemptId === attemptId);
      if (attempt) {
        Object.assign(attempt, updates);
        // If marking as complete, set completion timestamp
        if (updates.isComplete && !attempt.completedAt) {
          attempt.completedAt = new Date().toISOString();
        }
      }
    },
    REMOVE_LAB_ATTEMPT(state, attemptId) {
      const index = state.labAttempts.findIndex(
        (a) => a.attemptId === attemptId
      );
      if (index !== -1) {
        state.labAttempts.splice(index, 1);
      }
    },
    SET_LAB_ATTEMPTS(state, attempts) {
      state.labAttempts = attempts;
    },
    SET_CURRENT_ATTEMPT_ID(state, attemptId) {
      state.currentAttemptId = attemptId;
    },
    COMPLETE_LAB_ATTEMPT(state, attemptId) {
      const attempt = state.labAttempts.find((a) => a.attemptId === attemptId);
      if (attempt) {
        attempt.isComplete = true;
        attempt.completedAt = new Date().toISOString();
      }
    },
    SET_CONTEXT(state, context) {
      state.context = context;
    },
    SET_SHOW_DEBUG_UI(state, showDebugUI) {
      state.showDebugUI = showDebugUI;
    },
    SET_SHOW_SIMPLE_UI(state, showSimpleUI) {
      state.showSimpleUI = showSimpleUI;
    },
    SET_IN_MOBILE_VIEW(state, inMobileView) {
      state.inMobileView = inMobileView;
    },
    SET_LAB_MANIFEST(state, labManifest) {
      state.labManifest = labManifest;
    },
    SET_STORY_ENGINE_ACTIVE(state, active) {
      state.storyEngineActive = active;
    }
  },

  actions: {
    setContext({ commit }, context) {
      commit("SET_CONTEXT", context);
    },
    mapFromDatastore({ commit }, payload) {
      const normalized = normalizeDatastorePayloadFromRaw(payload);
      if (normalized == null) {
        return;
      }
      commit("SET_LAB_ATTEMPTS", normalized.labAttempts);
      if (normalized.currentAttemptId !== undefined) {
        commit("SET_CURRENT_ATTEMPT_ID", normalized.currentAttemptId);
      }
      if (normalized.context !== undefined && normalized.context !== null) {
        commit("SET_CONTEXT", normalized.context);
      }
      if (normalized.showSimpleUI) {
        commit("SET_SHOW_SIMPLE_UI", true);
      }
    },
    toggleTheme({ commit, state }) {
      const newTheme = state.theme === "light" ? "dark" : "light";
      commit("SET_THEME", newTheme);
    },
    initializeTheme({ commit, state }) {
      commit("SET_THEME", state.theme);
    },
    startNewLabAttempt({ commit, state }) {
      // Check if user has reached max attempts
      if (state.labAttempts.length >= state.maxAttempts) {
        throw new Error(`Maximum ${state.maxAttempts} attempts reached`);
      }

      const attemptId = `attempt_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      const attempt = {
        attemptId,
        timestamp: new Date().toISOString(),
        isComplete: false,
        completedAt: null,
        measurements: [],
        formData: {},
        solutions: {}
      };
      commit("ADD_LAB_ATTEMPT", attempt);
      commit("SET_CURRENT_ATTEMPT_ID", attemptId);
      return attemptId;
    },
    updateLabAttempt({ commit }, { attemptId, updates }) {
      commit("UPDATE_LAB_ATTEMPT", { attemptId, updates });
    },
    completeCurrentLabAttempt({ commit, state }) {
      if (state.currentAttemptId) {
        commit("COMPLETE_LAB_ATTEMPT", state.currentAttemptId);
      }
    },
    removeLabAttempt({ commit }, attemptId) {
      commit("REMOVE_LAB_ATTEMPT", attemptId);
    },
    clearLabAttempts({ commit }) {
      commit("SET_LAB_ATTEMPTS", []);
      commit("SET_CURRENT_ATTEMPT_ID", null);
    },
    setCurrentAttemptId({ commit }, attemptId) {
      commit("SET_CURRENT_ATTEMPT_ID", attemptId);
    },
    setShowDebugUI({ commit }, showDebugUI) {
      commit("SET_SHOW_DEBUG_UI", showDebugUI);
    },
    setShowSimpleUI({ commit }, showSimpleUI) {
      commit("SET_SHOW_SIMPLE_UI", showSimpleUI);
    },
    setInMobileView({ commit }, inMobileView) {
      commit("SET_IN_MOBILE_VIEW", inMobileView);
    },
    setLabManifest({ commit }, labManifest) {
      commit("SET_LAB_MANIFEST", labManifest);
    },
    setStoryEngineActive({ commit }, active) {
      commit("SET_STORY_ENGINE_ACTIVE", active);
    }
  },

  getters: {
    // Datastore mapper — updated to include lab attempts
    context: (state) => state.context,
    mapToDatastore(state) {
      return buildDatastorePayload({
        labAttempts: state.labAttempts,
        currentAttemptId: state.currentAttemptId,
        context: state.context,
        showSimpleUI: state.showSimpleUI
      });
    },
    theme: (state) => state.theme,
    labAttempts: (state) => state.labAttempts,
    currentAttemptId: (state) => state.currentAttemptId,
    maxAttempts: (state) => state.maxAttempts,
    getCurrentLabAttempt: (state) => {
      return state.currentAttemptId
        ? state.labAttempts.find(
            (attempt) => attempt.attemptId === state.currentAttemptId
          )
        : null;
    },
    getLabAttemptById: (state) => (attemptId) => {
      return state.labAttempts.find(
        (attempt) => attempt.attemptId === attemptId
      );
    },
    getLabAttemptsByStatus: (state) => (status) => {
      return state.labAttempts.filter((attempt) => attempt.status === status);
    },
    completedLabAttempts: (state) => {
      return state.labAttempts.filter((attempt) => attempt.isComplete === true);
    },
    incompleteLabAttempts: (state) => {
      return state.labAttempts.filter(
        (attempt) => attempt.isComplete === false
      );
    },
    locksOnCompletion: (state) => state.locksOnCompletion,
    canStartNewAttempt: (state) => {
      return state.labAttempts.length < state.maxAttempts;
    },
    attemptsRemaining: (state) => {
      return state.maxAttempts - state.labAttempts.length;
    },
    showDebugUI: (state) => {
      return import.meta.env.VITE_DEBUG === "true" && state.showDebugUI;
    },
    showSimpleUI: (state) => {
      return state.showSimpleUI || state.inMobileView;
    },
    inMobileView: (state) => {
      return state.inMobileView;
    },
    labManifest: (state) => state.labManifest,
    storyEngineActive: (state) => state.storyEngineActive,
    progressSteps: (state) => state.labManifest?.progressSteps ?? []
  }
};

export default app;
